<template>
    <section ref="partnersSection" aria-label="partners">
        <div class="slice-content">
            <div class="partners-container">
                <div class="partners-header">
                    <PrismicRichText :field="slice.primary.title" data-testid="partners-title" class="slice-title partners-header__title" />
                    <PrismicRichText
                        v-if="slice.primary.description"
                        :field="slice.primary.description"
                        class="slice-description partners-header__description"
                        data-testid="partners-description"
                    />

                    <div v-if="partnersCategories?.length" class="partners-categories">
                        <div v-for="(category, idx) in partnersCategories" :key="`${category}-${idx}`">
                            <CategoryPill
                                v-if="category"
                                :category="category"
                                :selected="selectedCategory === category"
                                @click="selectCategory(category)"
                            />
                        </div>
                    </div>
                </div>

                <div class="slice-body partners-cards">
                    <div v-for="(columns, idx) in partnersColumns" :key="idx">
                        <div v-for="(partner, index) in columns" :key="`${partner}-${index}`">
                            <Card :data-testid="`partner-card-${idx}`" class="partners-card">
                                <template #image>
                                    <PrismicImage :field="partner.logo" class="partners-card__logo" />
                                </template>
                                <PrismicRichText :field="partner.description" class="partners-card__description" />
                                <CardOffer :partner="partner" />
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {useContentHelpers} from '@nuxt-marketing-module/composables/useContentHelpers';
    import {getSliceComponentProps, PrismicRichText, PrismicImage} from '@prismicio/vue';
    import type {PartnersSlice, PartnersSliceDefaultPrimaryPartnersItem} from '@prismicio-types';
    import {useTranslation, useComponentTracking, useRoute, useRouter} from '#imports';
    import {useHydrationState} from '#imports';
    import {useMediaQuery} from '@malt/utils';
    import Card from '~/components/Card.vue';
    import CardOffer from './CardOffer.vue';
    import CategoryPill from './CategoryPill.vue';

    const props = defineProps(getSliceComponentProps<PartnersSlice>(['slice', 'index', 'slices', 'context']));

    const partnersSection = ref(null);

    const {matchMedia} = useMediaQuery();
    const isLargeScreen = computed(() => matchMedia.value.min.Md);
    const nbCols = computed(() => (isLargeScreen.value ? 3 : 2));

    const route = useRoute();
    const router = useRouter();

    const {t} = useTranslation();
    const All = t('landing.partners.category.all');

    function getPartnerCategories(category: string) {
        return category
            .split(';')
            .map((value) => value.trim())
            .filter(Boolean);
    }

    const partnersCategories = computed(() => {
        const categories = props.slice.primary.partners.flatMap((partner) => getPartnerCategories(partner.category ?? ''));
        return [All, ...new Set(categories)];
    });

    const {textToId} = useContentHelpers();
    function normalizeCategory(value: string) {
        return textToId(value);
    }

    const {isHydrated} = useHydrationState();

    const selectedCategory = computed(() => {
        if (!isHydrated.value) return All;

        const fragment = route.hash.replace(/^#/, '');
        if (!fragment) return All;

        return partnersCategories.value.find((category) => normalizeCategory(category ?? '') === fragment) ?? All;
    });

    const filteredPartners = computed(() =>
        selectedCategory.value === All
            ? props.slice.primary.partners
            : props.slice.primary.partners.filter((partner) => getPartnerCategories(partner.category ?? '').includes(selectedCategory.value)),
    );

    const partnersColumns = computed(() => {
        const cols: PartnersSliceDefaultPrimaryPartnersItem[][] = [[], [], []];
        filteredPartners.value.forEach((partner, index: number) => {
            cols[index % nbCols.value].push(partner);
        });
        return cols;
    });

    function selectCategory(category: string) {
        router.push({hash: `#${normalizeCategory(category)}`});
    }

    useComponentTracking(partnersSection);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .partners-header {
        &__title,
        &__description {
            display: flex;
            text-align: left;
            justify-content: flex-start;

            @include mq.screen_xs(min) {
                justify-content: center;
            }
        }
    }

    .partners-categories {
        margin-bottom: var(--joy-core-spacing-10);
        margin-top: var(--joy-core-spacing-9);
        display: flex;
        flex-wrap: wrap;
        gap: var(--joy-core-spacing-2);

        @include mq.screen_sm(min) {
            margin-bottom: 80px;
            margin-left: auto;
            margin-right: auto;
            max-width: 1130px;
        }

        @include mq.screen_xs(min) {
            justify-content: center;
        }
    }

    .partners-cards {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        width: 100%;
        gap: var(--joy-core-spacing-6);
        letter-spacing: -0.2px;

        @include mq.screen_sm(min) {
            gap: var(--joy-core-spacing-8);
        }
    }

    .partners-card {
        border-radius: var(--joy-core-radius-5) !important;
        margin-bottom: var(--joy-core-spacing-6);

        &:hover .partners-card__offer-description {
            max-height: 300px;
            transition: max-height 1s ease-in-out;
        }

        @include mq.screen_sm(min) {
            width: 370px;
            margin-bottom: var(--joy-core-spacing-8);
        }

        .card-image {
            img {
                max-width: 160px;
                max-height: 36px;
                object-fit: contain;
            }
        }

        .card-body {
            min-height: 257px;
            margin-top: 0;
            height: 100%;
            display: flex;
            flex-flow: column;
            justify-content: space-between;
        }
    }
</style>
